import React, { useEffect } from "react";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// Iter-95 Item 7 — ReceiptLightbox
// Full-screen image viewer used for expense receipt attachments.
// Replaces the previous `window.open(receiptImage, '_blank')` flow
// that navigated the user off to a black empty page. The lightbox
// stays inside the SPA, honours Escape + backdrop-click to close,
// and exposes Download + Print actions below the image.
// ============================================================
export function ReceiptLightbox({ src, filename, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while lightbox is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = src;
    a.download = filename || "receipt";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const handlePrint = () => {
    const w = window.open("");
    if (!w) return;
    w.document.write(`
      <!doctype html>
      <html><head><title>${filename || "Receipt"}</title>
      <style>body{margin:0;padding:0;background:#111;display:flex;align-items:center;justify-content:center;height:100vh}img{max-width:100%;max-height:100vh;object-fit:contain}</style>
      </head><body><img src="${src}" onload="window.print();window.close();"/></body></html>
    `);
    w.document.close();
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="receipt-lightbox"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 3000,
        background: "rgba(0,0,0,0.85)",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: 20, gap: 16,
      }}
    >
      <button
        type="button"
        data-testid="receipt-lightbox-close"
        onClick={onClose}
        aria-label="Close receipt"
        style={{
          position: "absolute", top: 18, right: 18,
          width: 40, height: 40, borderRadius: 999,
          background: "rgba(255,255,255,0.12)", color: "#fff",
          border: "1px solid rgba(255,255,255,0.35)",
          fontSize: 22, lineHeight: 1, cursor: "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}
      >×</button>
      <img
        src={src}
        alt={filename || "Receipt"}
        data-testid="receipt-lightbox-image"
        style={{
          maxWidth: "90vw", maxHeight: "85vh",
          objectFit: "contain",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          borderRadius: 8,
          background: "#111",
        }}
      />
      <div style={{ display: "flex", gap: 12 }}>
        <button
          type="button"
          className="btn"
          data-testid="receipt-lightbox-download"
          onClick={handleDownload}
          style={{
            background: "var(--kc-accent)", color: "#111",
            padding: "10px 20px", borderRadius: 8, border: "none",
            fontFamily: "'Cinzel',serif", fontWeight: 700,
            letterSpacing: "0.06em", textTransform: "uppercase",
            fontSize: 12, cursor: "pointer",
          }}
        >
          <CustomIcon name="download" size={14} style={{ marginRight: 6 }} /> Download
        </button>
        <button
          type="button"
          className="btn btn-outline"
          data-testid="receipt-lightbox-print"
          onClick={handlePrint}
          style={{
            padding: "10px 20px", borderRadius: 8,
            fontFamily: "'Cinzel',serif", fontWeight: 700,
            letterSpacing: "0.06em", textTransform: "uppercase",
            fontSize: 12,
          }}
        >
          <CustomIcon name="print" size={14} style={{ marginRight: 6 }} /> Print
        </button>
      </div>
    </div>
  );
}

// Derives a display filename from a receipt URI or base64 data-URL.
// Falls back to "receipt-<recordId>.jpg" when the value has no path.
export function receiptFilename(src, recordId, maxChars = 30) {
  let raw = "receipt";
  if (typeof src === "string") {
    if (src.startsWith("data:")) {
      // base64 data-URL — infer extension from mime type
      const m = src.match(/^data:image\/([a-z0-9+.-]+);/i);
      const ext = m ? m[1].split("+")[0] : "jpg";
      raw = `receipt-${recordId || "attachment"}.${ext}`;
    } else {
      const path = src.split(/[?#]/)[0];
      const parts = path.split("/");
      raw = parts[parts.length - 1] || `receipt-${recordId || "attachment"}`;
    }
  }
  if (raw.length <= maxChars) return raw;
  // Truncate with ellipsis while preserving the extension.
  const dot = raw.lastIndexOf(".");
  if (dot > 0) {
    const ext = raw.slice(dot);
    const head = raw.slice(0, Math.max(1, maxChars - ext.length - 1));
    return `${head}…${ext}`;
  }
  return `${raw.slice(0, maxChars - 1)}…`;
}
